import {
    AfterViewInit,
    Component,
    ElementRef,
    HostListener,
    NgZone,
    OnDestroy,
    OnInit,
    QueryList,
    TemplateRef,
    ViewChild,
    ViewChildren,
    inject,
    PLATFORM_ID,
    Renderer2
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { APP_CONSTANTS } from '../core/constants/enum.constants';
import { WebsocketService } from '../core/services/websocket.service';
import { CoinService } from '../core/services/coin.service';
import {
    Observable,
    Subscription,
    combineLatest,
    debounceTime,
    distinctUntilChanged,
    filter,
    forkJoin,
    map,
    merge,
    of,
    startWith,
    switchMap,
    take,
    tap,
    timer
} from 'rxjs';
import { IxfiCurrencyService, IxfiThemeService } from 'header';
import { getPrecision, openUrlInNewTab } from '../core/functions/utility';
import { environment } from 'src/environments/environment';
import { CoinDetail, ConvertPair, SwapPair, TradePair, WalletPair } from '../core/models/coin.model';
import { NewsPayload } from '../core/models/payload.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormControl } from '@angular/forms';
import { SortEvent, SortableDirective } from '../standalone/directives/sortable.directive';
import { DOCUMENT } from '@angular/common';
import { isPlatformBrowser } from '@angular/common';
import { ApiService } from '../core/services/api.service';
import { CoinInfoId, CoinMarketPair, CoinPriceId } from '../core/models/http.model';
import { PageTitleService } from '../core/services/page-title.service';
import { SCHEMA_NAMES } from '../core/constants/shared.constants';

declare const itbWidgetInit: Function;

const compare = (v1: any, v2: any) => (v1 < v2 ? -1 : v1 > v2 ? 1 : 0);

type SectionId = 'summary' | 'finance' | 'network' | 'ownership' | 'social' | 'markets' | 'news';

type WalletPairType = 'Buy' | 'Swap' | 'Convert' | 'Trade';

interface QuickLinks {
    website: string[];
    explorer: string[];
    community: string[];
    sourceCode: string[];
    documents: string[];
    tags: string[];
}

@Component({
    selector: 'app-coin',
    templateUrl: './coin.component.html',
    styleUrls: ['./coin.component.scss']
})
export class CoinComponent implements OnInit, OnDestroy {
    // Hold the HTML elements required to execute the scrollspy logic
    @ViewChildren('sectionElement') sectionElements!: QueryList<ElementRef<HTMLElement>>;
    @ViewChildren('navLinkElement') navLinkElements!: QueryList<ElementRef<HTMLAnchorElement>>;

    // Holds the <nav /> element reference of the section navbar
    @ViewChild('navbarElement', { static: true }) navbarElement!: ElementRef<HTMLElement>;

    // Holds the <input /> element reference of the swap, trade, and convert dropdowns
    @ViewChild('swapInputElement', { static: false }) private swapInputElement!: ElementRef<HTMLInputElement>;
    @ViewChild('tradeInputElement', { static: false }) private tradeInputElement!: ElementRef<HTMLInputElement>;
    @ViewChild('convertInputElement', { static: false }) private convertInputElement!: ElementRef<HTMLInputElement>;

    // Holds the tags popup template reference
    @ViewChild('tagsPopupElement') private tagsPopupElement!: TemplateRef<ElementRef>;
    // To Hold the Value of the Sortable Directive applied on table headers
    @ViewChildren(SortableDirective) headers?: QueryList<SortableDirective<CoinMarketPair>>;
    // Holds the parent container element of all the individual sections of the detail page
    @ViewChild('containerElement', { static: true }) containerElement!: ElementRef<HTMLDivElement>;

    // Component level variables
    isBrowser!: boolean;
    coinDetail!: CoinDetail;
    newsPayload!: NewsPayload;
    CDN_URL = environment.CDN_URL;
    subscriptions: Subscription[] = [];

    // Component level variables to hold currency related values
    usdExchangeRate: number = 1;
    currencySymbol: string = 'USD';

    // Component level variables used to asses and react to user scroll
    isNavbarSticky = false;
    private readonly SCROLL_OFFSET: number = 110;
    private readonly INITIAL_PAGE_HEIGHT: number = 120;
    private activeLink: HTMLAnchorElement | null = null;
    private activeSectionBounds: { top: number; bottom: number } | null = null;

    // Component level variables used in swap, trade, and convert dropdowns
    skeletonBackground!: string;
    isWalletPairsLoading: boolean = false;

    // ngModel variables for search fields of swap, trade and convert dropdowns
    swapSearch!: string;
    tradeSearch!: string;
    convertSearch!: string;
    // original and duplicate array variables for search implementation
    coinWalletPair!: WalletPair;
    originalPairs: {
        swapPairs: WalletPair['swap_coin_pairs'];
        convertPairs: WalletPair['convert_coin_pairs'];
        tradePairs: WalletPair['coin_pairs'];
    } = {
        swapPairs: [],
        tradePairs: [],
        convertPairs: []
    };
    filteredSwapPairs: SwapPair[] = [];
    filteredTradePairs: TradePair[] = [];
    filteredConvertPairs: ConvertPair[] = [];

    // Observable to run a delayed action on a piece of logic
    private readonly DELAY_TIMER: number = 200;
    // private delayAction$ = timer(this.DELAY_TIMER);

    // Variables used to handle Quick Links section
    urlDomain!: string;
    quickLinks!: QuickLinks;

    // Variables used to validate the data loaded in the itb widgets
    private scriptElement: HTMLScriptElement | null = null;
    private readonly MAX_INTERVAL_COUNT: number = 5;
    private readonly INTERVAL_TIMEOUT: number = 1250;
    private readonly VALID_DATA_CLASS: string = '.block-chart';
    private readonly INVALID_DATA_CLASS: string = '.empty-message-container';
    private readonly ITB_WIDGET_URL = 'https://app.intotheblock.com/widget.js';

    // Map used to control the visibility status of sections content and it's option in the navbar
    sectionVisibilityMap: { [key in SectionId]: { showInNav: boolean; showContent: boolean } } = {
        summary: { showInNav: true, showContent: false },
        finance: { showInNav: true, showContent: false },
        network: { showInNav: true, showContent: false },
        ownership: { showInNav: true, showContent: false },
        social: { showInNav: true, showContent: false },
        markets: { showInNav: true, showContent: false },
        news: { showInNav: true, showContent: true }
    };

    /**
     * Id mapping of Accordion contents that can be toggled (opened and closed)
     * Used to open accordion content when user clicks on a link in the coin header navbar
     */
    accordionContentJson: { [key in SectionId]: string } = {
        finance: 'finance-accordion-content',
        network: 'network-accordion-content',
        ownership: 'ownership-accordion-content',
        social: 'social-accordion-content',
        markets: 'market-accordion-content',
        // below accordions cannot be toggled
        summary: '',
        news: ''
    };
    /**
     * ID mapping of Accordion header buttons that can be toggled (opened and closed)
     * Used to open accordion content when user clicks on a link in the coin header navbar
     */
    accordionHeaderJson: { [key in SectionId]: string } = {
        finance: 'finance-accordion-header-btn',
        network: 'network-accordion-header-btn',
        ownership: 'ownership-accordion-header-btn',
        social: 'social-accordion-header-btn',
        markets: 'social-accordion-header-btn',
        // below accordions cannot be toggled
        summary: '',
        news: ''
    };

    //  Variables used to handle market pairs and their pagination, sorting, and searching.
    page = 1;
    marketPairs: CoinMarketPair[] = [];
    pageSize = +APP_CONSTANTS.TABLE_PAGE_LIMIT;
    searchControl = new FormControl<string>('');
    sortControl = new FormControl<SortEvent<CoinMarketPair>>(
        { direction: 'desc', column: 'marketCap' },
        { nonNullable: true }
    );
    newsData: any;
    // Services via dependency injection
    private zone = inject(NgZone);
    private ngbModal = inject(NgbModal);
    private document = inject(DOCUMENT);
    private renderer = inject(Renderer2);
    private route = inject(ActivatedRoute);
    private apiService = inject(ApiService);
    private platformId = inject(PLATFORM_ID);
    private coinService = inject(CoinService);
    private themeService = inject(IxfiThemeService);
    private websocketService = inject(WebsocketService);
    private currencyService = inject(IxfiCurrencyService);
    coinCode!: string | null;
    constructor(
        private router: Router,
        private metaService: PageTitleService,
        private renderer2: Renderer2
    ) {}
    ngOnInit(): void {
        this.isBrowser = isPlatformBrowser(this.platformId);
        // Check if sectionId is present
        // Check if sectionId is present
        const sectionId = this.route.snapshot.paramMap.get('section');
        // Use setTimeout to delay scrollToSection
        if (sectionId) {
            setTimeout(() => {
                this.scrollToSection(sectionId as SectionId);
            }, 500);
        }
        this.coinCode = this.route.snapshot.paramMap.get('coinCode');
        this.isWalletPairsLoading = true;
        this.metaService.updateMeta({
            'og:description': `Discover comprehensive research on cryptocurrency markets. Get detailed insights and analysis on crypto assets to enhance your trading strategy on IXFI.`,
            'og:image': `${this.CDN_URL}meta/Research_Meta-Image.png`,
            canonical: environment.IXFI_RESEARCH + '/coin/' + this.coinCode,
            keywords: `${this.coinCode} Research, IXFI Research Center, ${this.coinCode} Info, ${this.coinCode} Trading Strategy, ${this.coinCode} Detailed Analysis, ${this.coinCode} latest news`
        });
        this.addSchemaMarkup();
        const coinDetailSub: Subscription = this.coinService.coinDetailObs
            .pipe(
                filter((response): response is CoinDetail => response !== null),
                tap(response => {
                    this.coinDetail = response;
                    this.metaService.setTitle(`In-Depth Research on ${this.coinDetail.coin_name}`);
                    this.newsPayload = {
                        per_page_limit: +APP_CONSTANTS.SHORT_NEWS_PAGE_LIMIT,
                        coins: [this.coinDetail.coin_symbol.toUpperCase()]
                    };
                    this._initQuickLinks();
                }),
                switchMap(_ => merge(this._initItbWidgets(), this._initWalletPairs(), this._initMarketPairs()))
            )
            .subscribe();
        this.subscriptions.push(coinDetailSub);

        const newsDetailSub: Subscription = this.apiService.newsObs.subscribe({
            next: response => {
                this.newsData = response;
            },
            error: error => {
                // Handle any errors that occur during subscription
                console.error('Subscription Error:', error);
            }
        });
        this.subscriptions.push(newsDetailSub);
        const socketSub: Subscription = this.websocketService.coinInfoObs.subscribe({
            next: response => {
                if (this.coinDetail && response.coin_id === this.coinDetail.coin_id) {
                    this.coinDetail = {
                        ...this.coinDetail,
                        change: response.change,
                        is_up: response.is_up,
                        volume: response.volume,
                        price: response.coin_price_usd,
                        precision: getPrecision(response.coin_price_usd),
                        stats_24h: {
                            ...this.coinDetail.stats_24h,
                            price_change: this.coinDetail.price * ((this.coinDetail.change ?? 0) / 100)
                        }
                    };
                }
            }
        });
        this.subscriptions.push(socketSub);

        const currencySub: Subscription = this.currencyService.currencyObs
            .pipe(map(response => response.selectedCurrency))
            .subscribe({
                next: response => {
                    this.currencySymbol = response.currency_symbol.toUpperCase();
                    this.usdExchangeRate = this.currencySymbol === 'USD' ? 1 : response.usd_exchange_rate;
                }
            });
        this.subscriptions.push(currencySub);

        /**
         * Subscribe to the theme Observable to get the latest applied theme
         * If dark theme is applied, assign the loading skeleton's background color to grey
         * Else assign it back to empty string so that the loader displays default background color
         */
        const themeSub: Subscription = this.themeService.themeObs.subscribe({
            next: response => {
                if (response) this.skeletonBackground = 'grey';
                else this.skeletonBackground = 'lightgrey';
            }
        });
        this.subscriptions.push(themeSub);
    }

    private _initItbWidgets(): Observable<boolean> {
        return this.coinService.fetchItbAssets().pipe(
            map(itbAssets => itbAssets.includes(this.coinDetail.coin_symbol.toUpperCase())),
            tap(isValidItbCoin => {
                this.sectionVisibilityMap.summary = { showContent: isValidItbCoin, showInNav: isValidItbCoin };
                this.sectionVisibilityMap.finance = { showContent: isValidItbCoin, showInNav: isValidItbCoin };
                this.sectionVisibilityMap.network = { showContent: isValidItbCoin, showInNav: isValidItbCoin };
                this.sectionVisibilityMap.ownership = {
                    showContent: isValidItbCoin,
                    showInNav: isValidItbCoin
                };
                this.sectionVisibilityMap.social = { showContent: isValidItbCoin, showInNav: isValidItbCoin };
            }),
            filter(_ => this.isBrowser),
            switchMap(isValidItbCoin => {
                let resultObs: Observable<boolean> = of(false);
                if (isValidItbCoin)
                    resultObs = new Observable<boolean>(observer => {
                        this.scriptElement = this.renderer.createElement('script');
                        this.scriptElement!.defer = true;
                        this.scriptElement!.src = this.ITB_WIDGET_URL;

                        this.renderer.listen(this.scriptElement, 'load', () => {
                            observer.next(true);
                            observer.complete();
                        });

                        this.renderer.listen(this.scriptElement, 'error', () => {
                            console.warn('Could not load ITB script', this.scriptElement!.src);
                            observer.next(false);
                        });

                        this.renderer.appendChild(this.document.body, this.scriptElement);
                    });

                return resultObs;
            }),
            filter(response => response),
            tap(_ => {
                /**
                 * Initiate HTTP calls required by the ITB Widgets
                 * Run outside Angular Zone to improve performance
                 */
                this.zone.runOutsideAngular(() => {
                    itbWidgetInit({
                        apiKey: APP_CONSTANTS.ITB_API_KEY, // Initialize the widget with API key
                        options: {
                            tokenId: this.coinDetail.coin_symbol.toUpperCase(),
                            loader: true,
                            hideNavigator: true
                        }
                    });
                });

                /**
                 * The data will be asynchronously loaded onto the HTML as the itbWidgetInit() method is called
                 * Validate the ITB widgets to check if valid data is loaded.
                 */
                this.validateItbWidgets();
            })
        );
    }

    /**
     * Scans the HTML in the ITB widgets to check if the data being loaded is valid
     * Hides the widget if the data is invalid
     * Hides the entire section if all widgets of the section are invalid
     * Run the iteration for `maxIntervalCount` times per widget to check for validity
     * All checks are done in parallel rather than 1 at a time to improve performance
     */
    private validateItbWidgets(): void {
        // Map of ITB sections and their widgets, each associated with a unique data-type attribute
        const itbWidgetMap: { [key in SectionId]?: string[] } = {
            finance: ['in-and-out-of-the-money', 'break-even-price'],
            network: [
                'total-addresses',
                'daily-active-addresses',
                'transactions-volume',
                'number-of-transactions',
                'average-time-token-is-held',
                'concentration',
                'east-vs-west'
            ],
            ownership: ['addresses-by-time-held', 'balance-by-time-held', 'holdings-distribution-matrix'],
            social: ['telegram-sentiment', 'twitter-sentiment-v2', 'github-v2', 'search-trends']
        };

        /**
         * Map of ITB sections and the count of number of hidden widgets.
         * Used to assess whether to hide the entire section if all widgets are hidden
         */
        let hiddenWidgetsCount: { [key in SectionId]?: number } = {};

        for (let section in itbWidgetMap) {
            // Initialize the hidden widget count of the section to 0
            hiddenWidgetsCount[section] = 0;

            // Iterate over each attribute of the section
            const attributes: string[] = itbWidgetMap[section];
            attributes.forEach(attribute => {
                /**
                 * For each individual widget -
                 * Declare and initialize the interval count to 0
                 */
                let intervalCount: number = 0;

                // Start Interval
                const interval = window.setInterval(() => {
                    // Increment the interval count
                    intervalCount++;

                    /**
                     * Get the widget HTML Element whose `data-type` attribute value is the one in the loop
                     * Query from the `containerElement` which is the ITB content's parent <div>...</div>
                     */
                    const widgetElement: HTMLElement | null = this.containerElement.nativeElement.querySelector(
                        `[data-type="${attribute}"]`
                    );

                    // `widgetElement` is found, meaning, the ITB script has loaded the content onto the HTML element
                    if (widgetElement) {
                        // Query the `widgetElement` HTML to check for the noDataClass in its child DOM elements
                        const noDataElement: HTMLElement | null = widgetElement.querySelector(this.INVALID_DATA_CLASS);

                        // Query the `widgetElement` HTML to check for the validDataClass in its child DOM elements.
                        const dataPresentElement: HTMLElement | null = widgetElement.querySelector(
                            this.VALID_DATA_CLASS
                        );

                        // `noDataElement` is found, meaning, ITB has no data to fill this widget.
                        if (noDataElement) {
                            // Safely access the DOM through the renderer and hide the `widgetElement` from the UI
                            this.renderer.setStyle(widgetElement, 'display', 'none');

                            // Clear the interval as we got what we wanted to check.
                            clearInterval(interval);

                            // Increase the count of no. of widgets hidden for this section
                            hiddenWidgetsCount[section]++;

                            // If all the widgets in the section are hidden, hide the entire section and it's option in the nav
                            if (hiddenWidgetsCount[section] === attributes.length) {
                                this.sectionVisibilityMap[section].showContent = false;
                                this.sectionVisibilityMap[section].showInNav = false;
                            }
                        } else if (dataPresentElement) {
                            /**
                             * `dataPresentElement` is found, meaning, ITB has filled this widget with valid data
                             * Clear the interval as we got what we wanted to check.
                             */
                            clearInterval(interval);
                        }
                    }

                    /**
                     * In the last iteration of the setInterval()
                     *
                     * If the no. of intervals per widget exceed `maxIntervalCount` and we still haven't cleared from
                     * the interval from the logic above, it means the widget's HTML does not have either the noData
                     * or the validData class, meaning data is still in loading state.
                     *
                     * In this case - hide the widget, increment the hidden widget count, and clear the interval
                     */
                    if (intervalCount > this.MAX_INTERVAL_COUNT) {
                        this.renderer.setStyle(widgetElement, 'display', 'none');
                        hiddenWidgetsCount[section]++;
                        clearInterval(interval);

                        // If all the widgets in the section are hidden, hide the entire section and it's option in the nav
                        if (hiddenWidgetsCount[section] === attributes.length) {
                            this.sectionVisibilityMap[section].showContent = false;
                            this.sectionVisibilityMap[section].showInNav = false;
                        } else {
                            // Not all the widgets in this section are hidden, so do nothing
                        }
                    }
                }, this.INTERVAL_TIMEOUT); // runs every 1 second
            });
        }
    }

    private _initWalletPairs(): Observable<WalletPair> {
        const coinCodeInCaps: string = this.coinDetail.coin_symbol.toUpperCase();
        const payload: { coin_codes: string[] } = { coin_codes: [coinCodeInCaps] };
        return this.apiService.fetch<{ coinInfo: WalletPair[] }>('WALLET_PAIRS', payload, coinCodeInCaps).pipe(
            map(response => response.coinInfo[0]),
            tap(response => {
                this.isWalletPairsLoading = false;
                this.coinWalletPair = response;

                // this.originalPairs.swapPairs = this.coinWalletPair.swap_coin_pairs;
                this.originalPairs.convertPairs = this.coinWalletPair.convert_coin_pairs;
                this.originalPairs.tradePairs = this.coinWalletPair.coin_pairs;

                // this.filteredSwapPairs = this.coinWalletPair.swap_coin_pairs;
                this.filteredConvertPairs = this.coinWalletPair.convert_coin_pairs;
                this.filteredTradePairs = this.coinWalletPair.coin_pairs;
            })
        );
    }

    private _initQuickLinks(): void {
        this.quickLinks = { website: [], explorer: [], community: [], sourceCode: [], documents: [], tags: [] };
        if (this.coinDetail) {
            Object.entries(this.coinDetail.urls[0]).forEach(element => {
                if (element[0] === 'website') {
                    this.quickLinks.website = element[1];
                } else if (element[0] === 'explorer') {
                    this.quickLinks.explorer = element[1];
                } else if (['facebook', 'message_board', 'reddit', 'twitter'].includes(element[0])) {
                    this.quickLinks.community.push(...element[1]);
                } else if (element[0] === 'source_code') {
                    this.quickLinks.sourceCode = element[1];
                } else if (element[0] === 'technical_doc') {
                    this.quickLinks.documents = element[1];
                }
            });
            if (this.coinDetail.tags) this.quickLinks.tags = this.coinDetail.tags.map(tag => tag.replace(/-/g, ' '));

            if (this.quickLinks.website.length === 1) {
                const url = this.quickLinks.website[0];
                let domain = new URL(url);
                this.urlDomain = domain.hostname;
            }
        }
    }
    private scrollToSection(sectionId: SectionId) {
        setTimeout(() => {
            const sectionElement = document.getElementById(sectionId);
            if (sectionElement) {
                const offsetTop = sectionElement.offsetTop;
                window.scrollTo({ top: offsetTop - this.SCROLL_OFFSET, behavior: 'smooth' });
            }
        }, 0);
    }

    /**
     * The HostListener helps capture the scroll events of the window. We leverage this powerful tool to build
     * the scrollspy feature. There are 3 key operations to be managed.
     *
     * [Operation 1]: Dynamically adjust the active class of the navbar based on user's scroll position, thus
     * highlighting the section of the page currently in view.
     *
     * [Operation 2]: Making the navbar sticky after a certain scroll height is reached, thereby ensuring the
     * navbar remains visible and accessible as the user continues to scroll down the page.
     *
     * [Operation 3]: Removing the sticky property of the navbar and the active class from all navbar items
     * when the user returns to the top of the page
     *
     * @param event Event
     */
    @HostListener('window:scroll', [])
    scrollHandler() {
        /**
         * Operation #1: Toggle 'active' class on navbar items based on scroll positions
         *
         * This functionality is achieved in the most efficient way possible. Rather than calculating the active
         * link for every scroll, we maintain the boundaries of the current active <section> in the viewport and execute
         * logic only if the scroll values exceed these section boundaries.
         */

        // The current scroll position from the top of the page is fetched (window.scrollY)
        let top = window.scrollY;
        // `initialPageHeight` is added to account for the page's existing <header> component
        top += this.INITIAL_PAGE_HEIGHT;
        // If the scroll position is still within the bounds of the active section, no need to do anything
        if (this.activeSectionBounds && top >= this.activeSectionBounds.top && top <= this.activeSectionBounds.bottom) {
            return;
        }
        // HTML <section> element which has #sectionElement reference marked
        let currentSectionId: SectionId | null = null;

        for (const section of this.sectionElements) {
            const sectionElement: HTMLElement = section.nativeElement;
            /**
             * `offsetTop` gives the position of the <section> element from the top of the page
             * `offsetHeight` gives the height of the <section> element
             */
            const offsetTop: number = sectionElement.offsetTop;
            const offsetHeight: number = sectionElement.offsetHeight;
            /**
             * Checks if the current scroll position is within the vertical span of the current section in the loop
             * If yes, it implies that the looped <section> element is currently being visible on the viewport.
             */
            if (top >= offsetTop && top <= offsetTop + offsetHeight) {
                currentSectionId = sectionElement.id as SectionId;

                if (this.activeLink) this.renderer.removeClass(this.activeLink, 'active');
                // Find and activate the new link
                for (const link of this.navLinkElements) {
                    const navLinkElement: HTMLAnchorElement = link.nativeElement;
                    const hrefLink: string | null = navLinkElement.getAttribute('href');
                    if (hrefLink && hrefLink.includes(currentSectionId)) {
                        this.renderer.addClass(navLinkElement, 'active');
                        this.activeLink = navLinkElement;
                        break;
                    }
                }
                // Update the bounds of the active section
                this.activeSectionBounds = { top: offsetTop, bottom: offsetTop + offsetHeight };
                return;
            }
        }
        // Operation #2: Make navbar sticky after reaching certain scroll height
        const navHTMLElement: HTMLElement = this.navbarElement.nativeElement;
        /**
         * `offsetTop` gives the position of the <nav /> element from the top of the page
         * We're subtracting 60 from this value to account for coin detail section above the navbar that
         * you want to be scrolled off the screen before the navbar becomes sticky.
         */ let offsetTop: number = navHTMLElement.offsetTop;
        offsetTop -= 60;

        if (window.scrollY >= offsetTop) {
            this.renderer.addClass(navHTMLElement, 'sticky');
            this.isNavbarSticky = true;
        } else {
            this.renderer.removeClass(navHTMLElement, 'sticky');
            this.isNavbarSticky = false;
        }
        // Operation #3: Remove sticky and active classes when scrolled to the top
        if (window.scrollY === 0) {
            // Remove active class for all links
            this.navLinkElements.forEach(link => this.renderer.removeClass(link.nativeElement, 'active'));

            // Remove sticky class from navbar
            this.renderer.removeClass(navHTMLElement, 'sticky');

            // Set navbar sticky to false
            this.isNavbarSticky = false;
        }
    }

    /**
     * This function is called when user clicks on a nav item
     * @param sectionId Id of the <section> the user wants to go to
     */
    goToSection(sectionId: SectionId) {
        /**
         * [Task #1]: All <section> tags are embedded in accordions by default (except for summary) and user
         * may/may not close them Therefore when wants to navigate to a certain section, open the particular
         * accordion manually and safely by manipulating the DOM using the Renderer
         */
        // if (sectionId !== 'summary') {
        //     const headerId: string = this.accordionHeaderJson[sectionId];
        //     const contentId: string = this.accordionContentJson[sectionId];

        //     const buttonElement = this.containerElement.nativeElement.querySelector(`[id="${headerId}"]`);
        //     const contentElement = this.containerElement.nativeElement.querySelector(`[id="${contentId}"]`);

        //     this.renderer.removeClass(buttonElement, 'collapsed');
        //     this.renderer.addClass(contentElement, 'show');
        // }
        this.router.navigate(['/coins', this.coinCode, sectionId]).then(() => {
            this.scrollToSection(sectionId);
        });

        /**
         * [Task #2]: If the navbar is not sticky (meaning, we're not at the top of the page)
         * scroll the user to the particular section Id. Take into account the SCROLL_OFFSET
         */
        // if (!this.isNavbarSticky && this.isBrowser) {
        //     this.delayAction$.pipe(take(1)).subscribe({
        //         next: _ => {
        //             const sectionElement = this.containerElement.nativeElement.querySelector(`[id="${sectionId}"]`);
        //             if (sectionElement) {
        //                 const offsetTop = (<HTMLElement>sectionElement).offsetTop;
        //                 window.scrollTo({ top: offsetTop - this.SCROLL_OFFSET, behavior: 'smooth' });
        //             }
        //         }
        //     });
        // }
    }

    /**
     * This fnc is triggered in (input) event of the specified dropdown's search field
     * @param field Dropdown
     */
    filterText(field: WalletPairType) {
        const walletTypeJson: { [key in WalletPairType]: Function } = {
            Swap: (): void => {
                const searchText = this.swapSearch.toUpperCase();
                this.filteredSwapPairs = this.coinWalletPair.swap_coin_pairs.filter(
                    ele =>
                        ele.base_symbol.includes(searchText) ||
                        ele.quote_symbol.includes(searchText) ||
                        (ele.base_symbol + '/' + ele.quote_symbol).includes(searchText)
                );
            },
            Convert: (): void => {
                const searchText = this.convertSearch.toUpperCase();
                this.filteredConvertPairs = this.coinWalletPair.convert_coin_pairs.filter(
                    ele =>
                        ele.base_coin.includes(searchText) ||
                        ele.quote_coin.includes(searchText) ||
                        (ele.base_coin + '/' + ele.quote_coin).includes(searchText)
                );
            },
            Trade: (): void => {
                const searchText = this.tradeSearch.toUpperCase();
                this.filteredTradePairs = this.coinWalletPair.coin_pairs.filter(
                    ele =>
                        ele.base_coin_code.includes(searchText) ||
                        ele.quote_coin_code.includes(searchText) ||
                        (ele.base_coin_code + '/' + ele.quote_coin_code).includes(searchText)
                );
            },
            Buy: (): void => {}
        };
        walletTypeJson[field]();
    }

    /**
     * This fnc is called when user clicks on the various dropdown buttons
     * @param field Wallet Pair Type
     */
    toggleDropdownEvent(field: WalletPairType) {
        const walletTypeJson: { [key in WalletPairType]: Function } = {
            Swap: (): void => {
                this.swapInputElement.nativeElement.focus();
                this.filteredSwapPairs = this.coinWalletPair.swap_coin_pairs;
                this.swapSearch = '';
            },
            Convert: (): void => {
                this.convertInputElement.nativeElement.focus();
                this.filteredConvertPairs = this.coinWalletPair.convert_coin_pairs;
                this.convertSearch = '';
            },
            Trade: (): void => {
                this.tradeInputElement.nativeElement.focus();
                this.filteredTradePairs = this.coinWalletPair.coin_pairs;
                this.tradeSearch = '';
            },
            Buy: (): void => {}
        };
        walletTypeJson[field]();
    }

    redirectToExchange(field: WalletPairType, value: SwapPair | ConvertPair | TradePair) {
        let url: string = '';
        const walletTypeJson: { [key in WalletPairType]: Function } = {
            Swap: (): void => {
                const typeOffValue = value as SwapPair;
                url = `${environment.IXFI_EXCHANGE}swap/${typeOffValue.base_symbol}_${typeOffValue.quote_symbol}`;
            },
            Buy: (): void => {
                url = `${environment.IXFI_EXCHANGE}buy-crypto`;
            },
            Convert: (): void => {
                const typeOffValue = value as ConvertPair;
                url = `${environment.IXFI_EXCHANGE}convert/${typeOffValue.base_coin}_${typeOffValue.quote_coin}`;
            },
            Trade: (): void => {
                const typeOffValue = value as TradePair;
                url = `${environment.IXFI_EXCHANGE}trade/${typeOffValue.base_coin_code}_${typeOffValue.quote_coin_code}`;
            }
        };
        walletTypeJson[field]();
        openUrlInNewTab(url);
    }

    redirect(link: string) {
        openUrlInNewTab(link);
    }

    openTagsPopup() {
        this.ngbModal.open(this.tagsPopupElement, {
            size: 'md',
            centered: true,
            animation: true,
            backdrop: true
        });
    }

    private _initMarketPairs(): Observable<boolean> {
        const searchFieldChange$: Observable<string | null> = this.searchControl.valueChanges.pipe(
            startWith(this.searchControl.value),
            debounceTime(500),
            distinctUntilChanged(),
            tap(_ => (this.page = 1))
        );

        const sortFieldChange$: Observable<SortEvent<CoinMarketPair>> = this.sortControl.valueChanges.pipe(
            startWith(this.sortControl.value),
            map(({ column, direction }: SortEvent<CoinMarketPair>) => {
                if (direction === '') {
                    direction = 'desc';
                    column = 'marketCap';
                }
                this.headers?.forEach(header => {
                    if (header.sortable !== column) header.direction = '';
                });
                return { column, direction };
            }),
            tap(_ => (this.page = 1))
        );

        const socketStatus$ = this.websocketService.socketStatusObs.pipe(
            tap(response => {
                if (response) this.websocketService.joinRoom(APP_CONSTANTS.COIN_PAIR_PRICE_ROOM);
            })
        );
        const marketPairsSocketResponse$ = this.websocketService.marketPairObs.pipe(
            startWith(null),
            tap(response => {
                if (response) {
                    const matchedPair = this.marketPairs.find(pair => pair.pair_name === response.symbol);
                    if (matchedPair) {
                        matchedPair.last_price = response.last_price;
                        matchedPair.high_price = response.high_price;
                        matchedPair.low_price = response.low_price;
                        matchedPair.price_change = response.price_change;
                        matchedPair.price_change_percent = response.price_change_percent;
                        matchedPair.quote_volume = response.quote_volume;
                        matchedPair.volume = response.volume;
                    }
                }
            })
        );

        const ixfiCoinsHttp$ = this.apiService.fetch<CoinInfoId>('COIN_INFO_JSON');
        const ixfiCoinPricesHttp$ = this.apiService.fetch<CoinPriceId>('COIN_PRICE_JSON');
        const marketPairsHttp$: Observable<CoinMarketPair[]> = forkJoin([ixfiCoinsHttp$, ixfiCoinPricesHttp$]).pipe(
            switchMap(([ixfiCoinId, ixfiCoinPriceId]) => {
                const coinCodeInCaps: string = this.coinDetail.coin_symbol.toUpperCase();
                return this.apiService.fetch<{ docs: CoinMarketPair[] }>('MARKET_PAIRS', {}, coinCodeInCaps).pipe(
                    map(marketPairsResponse => {
                        const filteredPairs = marketPairsResponse.docs
                            .map(mPair => {
                                let quoteCoinPrice;
                                if (!Object(ixfiCoinPriceId[mPair.quote_coin]).hasOwnProperty('coin_price')) {
                                    console.warn('Quote not found', mPair, ixfiCoinPriceId);
                                    quoteCoinPrice = 1;
                                    return {
                                        ...mPair,
                                        baseCoinCode: 'N/A',
                                        baseCoinIcon: `coins/${ixfiCoinId[mPair.base_coin]?.coin_code}.png`,
                                        baseCoinName: 'N/A',
                                        quoteCoinCode: 'N/A',
                                        quoteCoinIcon: `coins/${ixfiCoinId[mPair.quote_coin]?.coin_code}.png`,
                                        quoteCoinName: 'N/A',
                                        priceInFiat: quoteCoinPrice * mPair.last_price,
                                        marketCap:
                                            (ixfiCoinId[mPair.base_coin].cs ?? 0) * (quoteCoinPrice * mPair.last_price)
                                    };
                                } else {
                                    quoteCoinPrice = ixfiCoinPriceId[mPair.quote_coin].coin_price;
                                    return {
                                        ...mPair,
                                        baseCoinCode: ixfiCoinId[mPair.base_coin].coin_code.toUpperCase(),
                                        baseCoinIcon: `coins/${ixfiCoinId[mPair.base_coin].coin_code}.png`,
                                        baseCoinName: ixfiCoinId[mPair.base_coin].coin_name,
                                        quoteCoinCode: ixfiCoinId[mPair.quote_coin].coin_code.toUpperCase(),
                                        quoteCoinIcon: `coins/${ixfiCoinId[mPair.quote_coin].coin_code}.png`,
                                        quoteCoinName: ixfiCoinId[mPair.quote_coin].coin_name,
                                        priceInFiat: quoteCoinPrice * mPair.last_price,
                                        marketCap:
                                            (ixfiCoinId[mPair.base_coin].cs ?? 0) * (quoteCoinPrice * mPair.last_price)
                                    };
                                }
                            })
                            .filter(
                                pair =>
                                    pair.baseCoinCode.toUpperCase() === this.coinDetail.coin_symbol.toUpperCase() ||
                                    pair.quoteCoinCode.toUpperCase() === this.coinDetail.coin_symbol.toUpperCase()
                            )
                            .sort((a, b) => (a.marketCap > b.marketCap ? -1 : 1));

                        /* Update the accordion display status based on data availability */
                        const showMarkets: boolean = filteredPairs.length > 0 ? true : false;
                        this.sectionVisibilityMap.markets = { showContent: showMarkets, showInNav: showMarkets };
                        return filteredPairs;
                    }),
                    tap(response => (this.marketPairs = response))
                );
            })
        );

        return combineLatest([
            socketStatus$,
            marketPairsHttp$,
            sortFieldChange$,
            searchFieldChange$,
            marketPairsSocketResponse$
        ]).pipe(
            map(([_, marketPairsHttp, sortField, searchText, __]) => {
                if (searchText !== null) {
                    const regex = new RegExp(searchText.toLowerCase(), 'i');
                    this.marketPairs = marketPairsHttp.filter(
                        pair =>
                            regex.test(pair.pair_name.toLowerCase()) ||
                            regex.test(pair.baseCoinCode.toLowerCase()) ||
                            regex.test(pair.baseCoinName.toLowerCase()) ||
                            regex.test(pair.quoteCoinCode.toLowerCase()) ||
                            regex.test(pair.quoteCoinName.toLowerCase()) ||
                            regex.test(pair.baseCoinCode + '/' + pair.quoteCoinCode)
                    );
                }
                this.marketPairs.sort((a, b) => {
                    const res = compare(a[sortField.column], b[sortField.column]);
                    return sortField.direction === 'asc' ? res : -res;
                });
                return true;
            })
        );
    }

    redirectToTrade(baseAsset: string, quoteAsset?: string) {
        const url = `${environment.IXFI_EXCHANGE}trade/${baseAsset}_${quoteAsset}`;
        openUrlInNewTab(url);
    }

    onPageChange(tableElement: HTMLElement) {
        tableElement.scrollIntoView();
    }

    /**
     * This func is called when user clicks on the sort icon on the column header
     * @param param0 {column: Column name, direction: Sorting type }
     */
    onSort({ column, direction }: SortEvent<CoinMarketPair>) {
        this.sortControl.setValue({ column, direction });
    }

    toggleNewsVisibility(event: 'hide' | 'show') {
        const showNews: boolean = event === 'show' ? true : false;
        this.sectionVisibilityMap.news = { showContent: showNews, showInNav: showNews };
    }

    addSchemaMarkup() {
        const coinCode = this.route.snapshot.paramMap.get('coinCode');
        const schemaUrl = this.router.url.slice(1);
        const schemaName = `${coinCode} Info`;
        const schemaData = {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
                {
                    '@type': 'ListItem',
                    position: 1,
                    name: 'IXFI',
                    item: environment.IXFI_EXCHANGE
                },
                {
                    '@type': 'ListItem',
                    position: 2,
                    name: SCHEMA_NAMES.HOME_PAGE,
                    item: environment.IXFI_RESEARCH
                },
                {
                    '@type': 'ListItem',
                    position: 3,
                    name: schemaName,
                    item: environment.IXFI_RESEARCH + schemaUrl
                }
            ]
        };
        this.metaService.addSchemaMarkup(JSON.stringify(schemaData), this.renderer2);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        if (isPlatformBrowser(this.platformId))
            this.websocketService.disconnectFromRoom(APP_CONSTANTS.COIN_PAIR_PRICE_ROOM);
        if (this.scriptElement) {
            this.renderer.removeChild(this.document.body, this.scriptElement);
            this.scriptElement = null;
        }
    }
}
